/**
 * tenantBrand — bind the SERVED per-tenant accent onto the design system's
 * brand custom properties.
 *
 * WHY THIS EXISTS
 * ---------------
 * jrni-tokens already ships per-tenant brand: `tokens.css` carries
 * `[data-tenant="cns"]`, `[data-tenant="retail"]`, … blocks that rebind
 * `--jrni-color-primary-1..4` and the brand aliases, and the dark block
 * repoints `--jrni-color-brand-fill` for a tenanted tree. NOTHING in the app
 * ever set that attribute (`grep -rn "data-tenant" app/src` → zero hits), so
 * every DS surface stayed on the default violet while screens painted the
 * SERVED accent (`customers-config.ts` → `accent` / `accentSoft` / `ink`, raw
 * per-tenant hex, the documented content exception) straight into inline
 * styles. Result: a Customers action modal rendered a tenant-accent header
 * band above violet selection chips — the band and the selections disagreed.
 *
 * The DS's own tenant blocks are a CLOSED set of slugs baked into tokens.css.
 * The served accent is open — any tenant, any hex — so the app cannot reuse
 * `[data-tenant]`. This module is the app-side bridge: it declares the SAME
 * custom properties, from served values, on a scope element. Every jrni-ui
 * selection primitive resolves its brand through `var()` at ITS OWN element
 * (`T.p1` = `var(--jrni-color-primary-1)`, `T.brandFill`, `T.p3`, `T.p4`), and
 * so do the `tokens.css` state layers (`[data-jrni-selected]` rules), so both
 * re-brand from one declaration with no fork and no jrni-ui change.
 *
 * WHY A <style> RULE AND NOT AN INLINE style OBJECT
 * -------------------------------------------------
 * Two reasons, both load-bearing:
 *
 * 1. DARK MODE. `tokens.css`'s dark block deliberately pins primary-2/3/4 to
 *    FIXED dark tints ("a tint derived from a mid-tone tenant brand would share
 *    its hue and fail contrast") while letting the tenant's primary-1 stay the
 *    accent AND become `brand-fill`. An inline style beats every author rule,
 *    so inline tints would paint a LIGHT tenant tint behind brand-coloured text
 *    on a dark card. A stylesheet can carry a dark-scoped rule that hands the
 *    tints back (`inherit` = the parent's value = the DS dark tint) while the
 *    hue-carrying tokens stay tenant. That reproduces the DS's own
 *    `[data-theme="dark"][data-tenant]` contract exactly.
 *
 * 2. ALIAS SUBSTITUTION. A custom property whose value contains `var()` is
 *    substituted at computed-value time ON THE ELEMENT THAT DECLARES IT, and
 *    descendants inherit the already-substituted result. `--jrni-color-brand-fill:
 *    var(--jrni-color-primary-1)` is declared on `:root`, so it froze to the
 *    DEFAULT violet there — rebinding primary-1 further down does not move it.
 *    That is exactly why every `[data-tenant]` block in tokens.css re-declares
 *    brand-primary / brand-fill / fill-primary-* by hand, and why the alias list
 *    below mirrors that block rather than relying on inheritance.
 *
 * Values are TOKENS or served DATA only — the derived tints are `color-mix`
 * against `--jrni-color-base-white`, never a hand-picked hex.
 */
import type { ReactNode } from "react";

/** The served brand triplet. `ink` is deliberately NOT consumed here — see the
 *  note on `TenantBrandScope`. Widened to accept the screen's own theme shapes
 *  (`IndustryConfig`, `CAtheme`) without a cast. */
export interface TenantBrandInput {
  /** The tenant's brand colour — becomes `--jrni-color-primary-1`. */
  accent: string;
  /** The tenant's soft tint, when served — becomes `--jrni-color-primary-3`. */
  accentSoft?: string;
}

/**
 * Served colour going into a stylesheet is an injection surface, so only a
 * plain CSS hex colour is accepted. Anything else (a `url(...)`, a `;`, a
 * function call) fails the test and the scope is skipped entirely rather than
 * sanitised into something half-applied.
 */
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const safe = (c: string | undefined): string | null =>
  typeof c === "string" && HEX.test(c.trim()) ? c.trim().toUpperCase() : null;

/**
 * One tint step of the brand ramp, mixed toward the base surface the tint sits
 * on. The percentages reproduce the DS's own ramp: measured against the
 * default brand (`#5142C7`), 20 / 12 / 6 % lands within ~3/255 per channel of
 * the shipped `--jrni-color-primary-2/3/4`.
 */
const tint = (accent: string, pct: number) =>
  `color-mix(in srgb, ${accent} ${pct}%, var(--jrni-color-base-white))`;

/** Stable, collision-resistant scope id from the served values (FNV-1a → b36). */
function scopeId(accent: string, soft: string | null): string {
  const s = accent + "|" + (soft ?? "");
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return "b" + h.toString(36);
}

/** The attribute jrni-ui knows nothing about — it only ever sees the tokens. */
const ATTR = "data-jrni-tenant-brand";

/**
 * The rules for one scope. Mirrors the alias list of a `[data-tenant]` block in
 * tokens.css one-for-one, then hands the TINTS back to the DS in dark.
 */
function scopeCss(id: string, accent: string, soft: string | null): string {
  const sel = `[${ATTR}="${id}"]`;
  const p3 = soft ?? tint(accent, 12);
  return (
    `${sel}{` +
      `--jrni-color-primary-1:${accent};` +
      `--jrni-color-primary-2:${tint(accent, 20)};` +
      `--jrni-color-primary-3:${p3};` +
      `--jrni-color-primary-4:${tint(accent, 6)};` +
      // Aliases tokens.css binds with var() ON :root, so they froze to the
      // default brand there and must be re-declared here (see header note 2).
      `--jrni-color-brand-primary:var(--jrni-color-primary-1);` +
      `--jrni-color-brand-primary-hover:var(--jrni-color-primary-2);` +
      `--jrni-color-brand-primary-muted:var(--jrni-color-primary-3);` +
      `--jrni-color-primary-text:var(--jrni-color-primary-1);` +
      `--jrni-color-brand-fill:var(--jrni-color-primary-1);` +
      `--jrni-color-fill-primary-1:var(--jrni-color-primary-1);` +
      `--jrni-color-fill-primary-2:var(--jrni-color-primary-3);` +
      `--jrni-color-fill-primary-3:var(--jrni-color-primary-4);` +
      `--jrni-color-utility-focus:var(--jrni-color-primary-1);` +
      `--jrni-color-brand-glow:color-mix(in srgb, ${accent} 30%, transparent);` +
      `--jrni-color-brand-glow-strong:color-mix(in srgb, ${accent} 35%, transparent);` +
    `}` +
    // Dark: the DS pins the brand TINTS to fixed dark tones and keeps the
    // tenant hue for primary-1 / brand-fill. `inherit` restores the parent's
    // (i.e. :root's dark) value, so this scope stops overriding them.
    `[data-theme="dark"] ${sel},[data-theme="dark"]${sel}{` +
      `--jrni-color-primary-2:inherit;` +
      `--jrni-color-primary-3:inherit;` +
      `--jrni-color-primary-4:inherit;` +
      `--jrni-color-brand-primary-hover:inherit;` +
      `--jrni-color-brand-primary-muted:inherit;` +
      `--jrni-color-fill-primary-2:inherit;` +
      `--jrni-color-fill-primary-3:inherit;` +
    `}`
  );
}

/**
 * Wrap any subtree so every jrni-ui brand surface inside it — selection chips,
 * segmented controls, day/slot pickers, selectable tiles, checked toggles,
 * focus rings, primary buttons — resolves to the SERVED tenant accent instead
 * of the DS default.
 *
 * `display: contents` means the wrapper generates no box: it never adds a node
 * to a flex/grid parent's layout, but it stays in the DOM tree, which is all
 * custom-property inheritance needs. Safe to nest, and safe to place inside a
 * portalled surface (a `Drawer` body, a `Modal`) — which is exactly why each
 * portal boundary gets its OWN scope rather than relying on the screen root.
 *
 * The served `ink` is deliberately NOT bound to `--jrni-color-on-brand`. The
 * served values are near-black SURFACES (`#1B1A18` / `#14161E` / `#14202B`,
 * used as the drawer hero background), not a foreground: binding them to
 * on-brand would put near-black text and glyphs on every solid brand fill in
 * the tree. `on-brand` stays the DS's fixed light ink, per the standing
 * "solid fill + light ink is a fixed token" rule.
 *
 * Renders children unchanged when no usable accent has been served, so a
 * screen that mounts before its vocabulary resolves is never half-branded.
 */
export function TenantBrandScope({ brand, children }: { brand: TenantBrandInput | null | undefined; children: ReactNode }) {
  const accent = safe(brand?.accent);
  if (!accent) return <>{children}</>;
  const soft = safe(brand?.accentSoft);
  const id = scopeId(accent, soft);
  return (
    <>
      {/* Scoped by attribute, so repeated mounts of the same tenant emit
          byte-identical (and therefore harmless) duplicate rules. */}
      <style>{scopeCss(id, accent, soft)}</style>
      <div {...{ [ATTR]: id }} style={{ display: "contents" }}>{children}</div>
    </>
  );
}
